import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { PlaylistTrack, Track } from '@app/core/models/Track';
import { RadioWatcherService } from '@app/core/ngxs/radio-watcher.service';
import { DataResolved } from '@app/core/resolvers/generic-resolver.resolver';
import { AudioState } from '@app/core/states/audio.state';
import { BacPlaylistModalComponent } from '@app/library/components/modals/bac-playlist-modal/bac-playlist-modal.component';
import { PlaylistContextMenuComponent } from '@app/library/components/modals/playlist-context-menu/playlist-context-menu.component';
import { Playlist } from '@app/library/models/playlist.model';
import { SearchService } from '@app/library/services/search.service';
import {
  PlaylistTracksFailure,
  PlaylistTracksRequest,
  PlaylistTracksSuccess,
} from '@app/library/states/playlist-tracks.actions';
import { PlaylistTracksState } from '@app/library/states/playlist-tracks.state';
import { StartEditPlaylist } from '@app/library/states/playlists.actions';
import { PlaylistsState } from '@app/library/states/playlists.state';
import { MOBILE_DIALOG_CONFIG } from '@app/shared/constants';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import {
  MobileSidenavService,
  UiStoreHelperService,
} from '@radioking/shared/common-services';
import { SearchBarComponent } from '@radioking/ui/input';
import { isArray } from 'lodash';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  switchMapTo,
  take,
  tap,
} from 'rxjs/operators';
import { MobDailyGenAddTrackComponent } from '../../../../../../../../libs/daily-generation/src/lib/components/modals/mob-daily-gen-add-track/mob-daily-gen-add-track.component';

@Component({
  selector: 'rk-playlist-mobile',
  templateUrl: './playlist-mobile.component.html',
  styleUrls: ['./playlist-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaylistMobileComponent implements OnInit, OnDestroy {
  @Select(AudioState.currentTrackPlaying)
  currentTrack$: Observable<Track>;

  @ViewChild('searchBar')
  searchBar: SearchBarComponent;

  playlist$: Observable<Playlist>;
  $playlist: WritableSignal<boolean> = signal(false);

  tracks$: Observable<PlaylistTrack[]>;

  isLoading$: Observable<boolean>;

  tracksUnfiltered$: Observable<PlaylistTrack[]>;

  searchSubject = new BehaviorSubject<string>('');

  isProcessing$: Observable<boolean>;

  subscription = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly watcher: RadioWatcherService,
    private readonly searchService: SearchService,
    private readonly helper: UiStoreHelperService,
    private readonly mobileSidenavService: MobileSidenavService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.playlist$ = this.route.data.pipe(
      map<any, DataResolved<Playlist>>(val => val.playlist),
      tap(() => {
        this.mobileSidenavService.close();
        this.clearSearch();
      }),
      switchMap(val => val.data$),
      filter(val => typeof val !== 'undefined'),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.subscription.add(
      this.playlist$.pipe(filter(p => !!p)).subscribe(p => {
        this.$playlist.set(true);
        this.startEditPlaylist(p);
      }),
    );

    this.isLoading$ = this.helper.getLoadingState(
      PlaylistTracksRequest,
      [PlaylistTracksFailure, PlaylistTracksSuccess],
      true,
    );

    this.tracksUnfiltered$ = this.playlist$.pipe(
      switchMap(playlist =>
        this.store
          .select(PlaylistTracksState.playlistTracks)
          .pipe(map(fn => fn(playlist.id))),
      ),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.tracks$ = this.searchService
      .createTracksSearch(this.tracksUnfiltered$, this.searchSubject.asObservable())
      .pipe(
        filter(val => isArray(val)),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

    this.isProcessing$ = this.playlist$.pipe(
      filter(pl => pl.type === 'dynamic'),
      switchMap(playlist =>
        this.store
          .select(PlaylistsState.playlistProcessing)
          .pipe(map(fn => fn(playlist.id))),
      ),
    );

    this.watchForProcessingChange();

    const tracksRequestSubscription = this.playlist$
      .pipe(
        map(playlist => playlist.id),
        distinctUntilChanged(),
        switchMap(id =>
          this.watcher.dispatchAndWatchAsObservable(new PlaylistTracksRequest(id)),
        ),
      )
      .subscribe(() => {});
    this.subscription.add(tracksRequestSubscription);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  clearSearch() {
    if (this.searchBar) {
      this.searchBar.value = '';
    }
    this.searchSubject.next('');
  }

  onChangeSearch(str: string) {
    if (typeof str === 'string') {
      this.searchSubject.next(str);
    }
  }

  watchForProcessingChange() {
    let isProcessStarted = false;

    const process = this.isProcessing$
      .pipe(
        distinctUntilChanged(),
        map<boolean, [boolean, boolean]>(val => {
          if (val) {
            isProcessStarted = true;

            return [val, true];
          } else {
            return [val, isProcessStarted];
          }
        }),
        map<[boolean, boolean], boolean>(([isProcessing, hasBeenProcessing]) => {
          if (hasBeenProcessing && !isProcessing) {
            isProcessStarted = false;

            return true;
          }

          return false;
        }),
        filter(val => val),
        switchMapTo(this.playlist$.pipe(take(1))),
      )
      .subscribe(playlist => {
        this.store.dispatch(new PlaylistTracksRequest(playlist.id));
      });

    this.subscription.add(process);
  }

  @Dispatch()
  startEditPlaylist(playlist: Playlist) {
    return new StartEditPlaylist(playlist);
  }

  openContextMenu() {
    this.dialog.open(PlaylistContextMenuComponent, MOBILE_DIALOG_CONFIG);
  }

  openBacPlaylistModal() {
    this.mobileSidenavService.open(BacPlaylistModalComponent);
  }

  addTracks() {
    this.mobileSidenavService.open(MobDailyGenAddTrackComponent, {
      bgType: 'gray',
      data: {
        isPlaylistForm: true,
      },
    });
  }
}
